import React from 'react';
import { FaBuilding, FaBullseye, FaFlask, FaGlobe, FaShieldHalved, FaScrewdriverWrench } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';

type AboutCard = {
  icon: React.ComponentType<{ className?: string }>;
  titleKey: string;
  descriptionKey: string;
  highlight?: boolean;
};

const cards: AboutCard[] = [
  {
    icon: FaShieldHalved,
    titleKey: 'aboutPageSection.different.items.0.title',
    descriptionKey: 'aboutPageSection.different.items.0.description'
  },
  {
    icon: FaGlobe,
    titleKey: 'aboutPageSection.different.items.1.title',
    descriptionKey: 'aboutPageSection.different.items.1.description'
  },
  {
    icon: FaScrewdriverWrench,
    titleKey: 'aboutPageSection.different.items.2.title',
    descriptionKey: 'aboutPageSection.different.items.2.description',
    highlight: true
  },
  {
    icon: FaBuilding,
    titleKey: 'aboutPageSection.different.items.3.title',
    descriptionKey: 'aboutPageSection.different.items.3.description'
  },
  {
    icon: FaFlask,
    titleKey: 'aboutPageSection.different.items.4.title',
    descriptionKey: 'aboutPageSection.different.items.4.description'
  },
  {
    icon: FaBullseye,
    titleKey: 'aboutPageSection.different.items.5.title',
    descriptionKey: 'aboutPageSection.different.items.5.description'
  }
];

const AboutDifferent: React.FC = () => {
  const { t } = useTranslation();
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const updateProgress = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const viewportH = window.innerHeight || 1;
      const total = rect.height + viewportH * 0.35;
      const traveled = viewportH * 0.75 - rect.top;
      const ratio = Math.max(0, Math.min(1, traveled / total));
      setProgress(ratio);
    };

    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    return () => {
      window.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-[#0F172A] tracking-tight">{t('aboutPageSection.different.title')}</h2>
          <p className="text-[#64748B] mt-3 max-w-xl mx-auto">
            {t('aboutPageSection.different.description')}
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-200" />
          <div
            className="absolute left-6 top-0 w-px bg-gradient-to-b from-teal-700 via-teal-600 to-teal-500 transition-[height] duration-150"
            style={{ height: `${Math.round(progress * 100)}%` }}
          />
          <div className="space-y-8">
            {cards.map((item) => (
              <article key={item.titleKey} className="relative pl-16">
                <div className="absolute left-0 top-0 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-white to-teal-50 text-teal-700 ring-1 ring-teal-300 shadow-[0_8px_18px_rgba(13,148,136,0.18)]">
                  <item.icon className="h-4 w-4" />
                </div>
                <h3 className="font-bold text-[#0F172A] text-lg leading-tight">{t(item.titleKey)}</h3>
                <p className="text-[#64748B] text-sm mt-2 leading-relaxed max-w-3xl">{t(item.descriptionKey)}</p>
                <div className={`mt-5 h-px ${item.highlight ? 'bg-teal-200' : 'bg-[#E2E8F0]'}`} />
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutDifferent;
